import { useState } from 'react';
import { Link as RouterLink, useParams } from '@tanstack/react-router';
import type {
  ModelStatus,
  OrgRole,
  ProjectImage,
  ProjectModel,
  ProjectVideo,
  VideoStatus,
} from '@ddp/shared';
import Tag from '@carbon/react/es/components/Tag/Tag';
import Button from '@carbon/react/es/components/Button';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import TrashCan from '@carbon/icons-react/es/TrashCan';

import { useAuthStore } from '../auth/store.js';
import { useT } from '../i18n/index.js';
import { PageHeader } from '../components/PageHeader.js';
import { useProjectDetail } from '../features/projects/use-project-detail.js';
import { ObjectivePanel } from '../features/objectives/ObjectivePanel.js';

type TagType = 'blue' | 'green' | 'red' | 'gray';

/** Hiérarchie des rôles : retirer un objet d'un projet exige EDITOR+ (comme l'API). */
const ROLE_RANK: Record<OrgRole, number> = {
  VIEWER: 0,
  ANNOTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};

const STATUS_TYPE: Record<VideoStatus, TagType> = {
  PENDING: 'gray',
  UPLOADING: 'blue',
  UPLOADED: 'blue',
  PROCESSING: 'blue',
  READY: 'green',
  FAILED: 'red',
};

const MODEL_STATUS_TYPE: Record<ModelStatus, TagType> = {
  PENDING: 'gray',
  READY: 'green',
  FAILED: 'red',
};

/**
 * Détail d'un projet **transverse** (ADR-0024, incrément 2c) : ses vidéos, images et modèles membres,
 * chacun relié à sa page, avec un retrait réservé au rôle éditeur. Le contenu se peuple depuis « Data »
 * et « Model » (bouton « Ajouter au projet… »). Une ressource étrangère renvoie 404 (cloisonnement).
 */
export function ProjectDetailPage() {
  const t = useT();
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const { project, loading, notFound, removeVideo, removeImage, removeModel } =
    useProjectDetail(projectId);
  const role = useAuthStore((state) => state.currentUser?.memberships[0]?.role ?? null);
  const canOrganize = role !== null && ROLE_RANK[role] >= ROLE_RANK.EDITOR;
  // Arrivée depuis « Démarrer un objectif projet » : le fragment ouvre directement le formulaire.
  const autoOpenObjective = typeof window !== 'undefined' && window.location.hash === '#objectif';

  const statusLabels: Record<VideoStatus, string> = {
    PENDING: t('data.status.pending'),
    UPLOADING: t('data.status.uploading'),
    UPLOADED: t('data.status.uploaded'),
    PROCESSING: t('data.status.processing'),
    READY: t('data.status.ready'),
    FAILED: t('data.status.failed'),
  };
  const modelStatusLabels: Record<ModelStatus, string> = {
    PENDING: t('model.status.pending'),
    READY: t('model.status.ready'),
    FAILED: t('model.status.failed'),
  };

  const isEmpty =
    !!project &&
    project.videos.length === 0 &&
    project.images.length === 0 &&
    project.models.length === 0;

  return (
    <section className="ddp-page__section">
      <RouterLink to="/" className="ddp-page-back">
        ← {t('overview.projects.title')}
      </RouterLink>

      {notFound ? (
        <>
          <PageHeader title={t('project.notFound.title')} />
          <p className="ddp-videos__empty">{t('project.notFound.body')}</p>
        </>
      ) : (
        <>
          <PageHeader title={project?.name ?? '…'} lead={project?.description ?? undefined} />

          <ObjectivePanel
            projectId={projectId}
            canManage={canOrganize}
            autoOpen={autoOpenObjective}
          />

          {!loading && isEmpty && <p className="ddp-videos__empty">{t('project.empty')}</p>}

          {project && project.videos.length > 0 && (
            <>
              <h2 className="ddp-project-detail__subtitle">
                {t('data.section.videos')} ({project.videos.length})
              </h2>
              <ul className="ddp-project-detail__list">
                {project.videos.map((video) => (
                  <ProjectVideoRow
                    key={video.videoId}
                    video={video}
                    statusLabel={statusLabels[video.status]}
                    statusType={STATUS_TYPE[video.status]}
                    canOrganize={canOrganize}
                    onRemove={removeVideo}
                  />
                ))}
              </ul>
            </>
          )}

          {project && project.images.length > 0 && (
            <>
              <h2 className="ddp-project-detail__subtitle">
                {t('data.section.images')} ({project.images.length})
              </h2>
              <ul className="ddp-project-detail__list">
                {project.images.map((image) => (
                  <ProjectImageRow
                    key={image.imageId}
                    image={image}
                    canOrganize={canOrganize}
                    onRemove={removeImage}
                  />
                ))}
              </ul>
            </>
          )}

          {project && project.models.length > 0 && (
            <>
              <h2 className="ddp-project-detail__subtitle">
                {t('model.registry.title')} ({project.models.length})
              </h2>
              <ul className="ddp-project-detail__list">
                {project.models.map((model) => (
                  <ProjectModelRow
                    key={model.modelId}
                    model={model}
                    statusLabel={modelStatusLabels[model.status]}
                    statusType={MODEL_STATUS_TYPE[model.status]}
                    canOrganize={canOrganize}
                    onRemove={removeModel}
                  />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Une ligne du détail : l'objet (lien vers sa page), son statut, sa date d'ajout et — pour un
 * éditeur — un retrait. Le retrait porte son propre état d'erreur pour ne pas masquer les autres.
 */
function ProjectVideoRow({
  video,
  statusLabel,
  statusType,
  canOrganize,
  onRemove,
}: {
  video: ProjectVideo;
  statusLabel: string;
  statusType: TagType;
  canOrganize: boolean;
  onRemove: (videoId: string) => Promise<void>;
}) {
  const t = useT();
  const remove = useRemove(() => onRemove(video.videoId));

  return (
    <li className="ddp-project-detail__row">
      <RouterLink
        to="/videos/$videoId"
        params={{ videoId: video.videoId }}
        className="ddp-project-detail__title"
        title={video.title}
      >
        {video.title}
      </RouterLink>
      <Tag className="ddp-project-detail__status" type={statusType} size="sm">
        {statusLabel}
      </Tag>
      <span className="ddp-project-detail__added">
        {t('project.videos.addedOn')} {formatDate(video.addedAt)}
      </span>
      {canOrganize && <RemoveButton busy={remove.busy} onClick={remove.run} />}
      {remove.error && <RowError message={remove.error} />}
    </li>
  );
}

/** Une image membre : lien vers la vidéo source (annotation), sa date d'ajout, et un retrait. */
function ProjectImageRow({
  image,
  canOrganize,
  onRemove,
}: {
  image: ProjectImage;
  canOrganize: boolean;
  onRemove: (imageId: string) => Promise<void>;
}) {
  const t = useT();
  const remove = useRemove(() => onRemove(image.imageId));

  return (
    <li className="ddp-project-detail__row">
      <RouterLink
        to="/videos/$videoId"
        params={{ videoId: image.videoId }}
        className="ddp-project-detail__title"
        title={image.videoTitle}
      >
        {t('project.images.of')} {image.videoTitle}
      </RouterLink>
      <span className="ddp-project-detail__added">
        {t('project.videos.addedOn')} {formatDate(image.addedAt)}
      </span>
      {canOrganize && <RemoveButton busy={remove.busy} onClick={remove.run} />}
      {remove.error && <RowError message={remove.error} />}
    </li>
  );
}

/** Un modèle membre : nom + version, son statut, sa date d'ajout, et un retrait. */
function ProjectModelRow({
  model,
  statusLabel,
  statusType,
  canOrganize,
  onRemove,
}: {
  model: ProjectModel;
  statusLabel: string;
  statusType: TagType;
  canOrganize: boolean;
  onRemove: (modelId: string) => Promise<void>;
}) {
  const t = useT();
  const remove = useRemove(() => onRemove(model.modelId));

  return (
    <li className="ddp-project-detail__row">
      <span className="ddp-project-detail__title">
        {model.name} · v{model.version}
      </span>
      <Tag className="ddp-project-detail__status" type={statusType} size="sm">
        {statusLabel}
      </Tag>
      <span className="ddp-project-detail__added">
        {t('project.videos.addedOn')} {formatDate(model.addedAt)}
      </span>
      {canOrganize && <RemoveButton busy={remove.busy} onClick={remove.run} />}
      {remove.error && <RowError message={remove.error} />}
    </li>
  );
}

/** Bouton de retrait (icône poubelle), commun aux trois types de lignes. */
function RemoveButton({ busy, onClick }: { busy: boolean; onClick: () => void }) {
  const t = useT();
  return (
    <Button
      kind="danger--ghost"
      size="sm"
      hasIconOnly
      disabled={busy}
      renderIcon={TrashCan}
      iconDescription={t('project.item.remove')}
      tooltipPosition="left"
      onClick={onClick}
    />
  );
}

function RowError({ message }: { message: string }) {
  const t = useT();
  return (
    <InlineNotification
      kind="error"
      title={t('data.error.title')}
      subtitle={message}
      lowContrast
      hideCloseButton
    />
  );
}

/** Petit hook de retrait : état occupé/erreur partagé par les trois lignes. */
function useRemove(action: () => Promise<void>) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = () => {
    setBusy(true);
    setError(null);
    action().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : t('data.error.title'));
      setBusy(false);
    });
  };
  return { busy, error, run };
}

function formatDate(value: Date): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
